import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Phone,
  Mail,
  Plus,
  Trash2,
  RefreshCw,
  Building2,
  User,
  MapPin,
  Truck,
  ExternalLink,
  Send,
  UserCheck,
  Loader2,
  MessageSquare,
  Edit,
  Calendar,
  Hash,
  FileText,
  Activity,
  UserCog,
  Eye,
  CheckCircle2,
  XCircle,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { StatusBadge } from '@/components/common/StatusBadge'
import { PageLoader } from '@/components/common/LoadingSpinner'
import { leadsApi } from '@/api/endpoints/leads'
import { activitiesApi } from '@/api/endpoints/activities'
import { usersApi } from '@/api/endpoints/users'
import { formatRelativeTime } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'
import { getErrorMessage } from '@/api/client'
import type { Lead, LeadFilters, LeadStatus, Activity as ActivityType } from '@/types'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'

// Status values that map to backend API
const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'low_priority', label: 'Low Priority' },
  { value: 'disqualified', label: 'Disqualified' },
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'existing_client', label: 'Existing Client' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'not_contacted', label: 'Not Contacted' },
  { value: 'closed_lost', label: 'Closed Lost' },
]

// Qualification status values
const QUALIFICATION_OPTIONS = [
  { value: 'qualified', label: 'Qualified' },
  { value: 'low_priority', label: 'Low Priority' },
  { value: 'needs_review', label: 'Needs Review' },
  { value: 'disqualified', label: 'Disqualified' },
  { value: 'existing_client', label: 'Existing Client' },
  { value: 'not_existing_client', label: 'Not Existing Client' },
]

// Lead type values - these use lead_type filter (semantic classification)
const LEAD_TYPE_VALUES = ['newly_applied', 'newly_approved', 'approved_90_days']

// Lead source options - values must match database source or lead_type column
const SOURCE_OPTIONS = [
  // Lead types (use lead_type filter for semantic classification)
  { value: 'newly_applied', label: 'Newly Applied' },
  { value: 'newly_approved', label: 'Newly Approved' },
  // Specific sources (use source filter)
  { value: 'approved_30_days', label: 'Approved 30 Days' },
  { value: 'approved_60_days', label: 'Approved 60 Days' },
  { value: 'approved_90_days', label: 'Approved 90 Days' },
  { value: 'approved_180_days', label: 'Approved 180 Days' },
  { value: 'applied_30_days', label: 'Applied 30 Days' },
  { value: 'applied_60_days', label: 'Applied 60 Days' },
  { value: 'applied_90_days', label: 'Applied 90 Days' },
  { value: 'applied_180_days', label: 'Applied 180 Days' },
  { value: 'fmcsa', label: 'FMCSA' },
  { value: 'fmcsa_api', label: 'FMCSA API' },
  { value: 'fmcsa_file', label: 'FMCSA File' },
  // Marketing sources
  { value: 'webform', label: 'Web Form' },
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'facebook_ads', label: 'Facebook Ads' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'webtel', label: 'Webtel' },
  // Manual/Sales sources
  { value: 'manual', label: 'Manual' },
  { value: 'referral', label: 'Referral' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'trade_show', label: 'Trade Show' },
  { value: 'website', label: 'Website' },
  // Industry-specific
  { value: 'staffing', label: 'Staffing' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'construction', label: 'Construction' },
  { value: 'wholesale', label: 'Wholesale' },
  { value: 'oilfield', label: 'Oilfield' },
  { value: 'government', label: 'Government' },
  { value: 'general', label: 'General' },
  { value: 'other', label: 'Other' },
]

const LEAD_STATUSES: LeadStatus[] = ['new', 'contacted', 'qualified', 'unqualified', 'converted', 'lost', 'low_priority']
const PAGE_SIZES = [10, 25, 50, 100, 250]

type SortField = 'company_name' | 'created_at' | 'status' | 'quality_score' | 'last_contacted_at'
type SortOrder = 'asc' | 'desc'

export function Leads() {
  const queryClient = useQueryClient()

  // Filter and pagination state
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [qualificationFilter, setQualificationFilter] = useState<string>('all')
  const [salespersonFilter, setSalespersonFilter] = useState<string>('all')
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [requiresReviewFilter, setRequiresReviewFilter] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  // Debounced search - only update search after 500ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null)
  const [showBulkActionDialog, setShowBulkActionDialog] = useState(false)
  const [bulkAction, setBulkAction] = useState<'status' | 'delete' | 'push' | 'reassign' | null>(null)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [newStatus, setNewStatus] = useState<LeadStatus>('new')
  const [pushQueue, setPushQueue] = useState('engagement')
  const [bulkPushLeadType, setBulkPushLeadType] = useState('')
  const [bulkPushChannel, setBulkPushChannel] = useState('')
  const [bulkPushLanguage, setBulkPushLanguage] = useState('')
  const [bulkBypassCooldown, setBulkBypassCooldown] = useState(false)
  const [showPushDialog, setShowPushDialog] = useState(false)
  const [leadToPush, setLeadToPush] = useState<Lead | null>(null)
  const [singlePushQueue, setSinglePushQueue] = useState('engagement')
  const [pushLeadType, setPushLeadType] = useState('')
  const [pushChannel, setPushChannel] = useState('')
  const [pushLanguage, setPushLanguage] = useState('')
  const [bypassCooldown, setBypassCooldown] = useState(false)

  // View details dialog state
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)

  // Edit lead dialog state
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [leadToEdit, setLeadToEdit] = useState<Lead | null>(null)
  const [editLeadForm, setEditLeadForm] = useState({
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
    mc_number: '',
    dot_number: '',
    state: '',
    city: '',
    source: '',
    notes: '',
    status: 'new' as LeadStatus,
  })

  // Add lead form state
  const [addLeadForm, setAddLeadForm] = useState({
    company_name: '',
    contact_name: '',
    phone: '',
    email: '',
    mc_number: '',
    dot_number: '',
    state: '',
    city: '',
    zip_code: '',
    source: 'manual',
    notes: '',
    submit_to_qualifier: false,
  })

  // Build filters object - maps directly to backend API params
  const filters: LeadFilters = useMemo(() => ({
    search: search || undefined,
    // Status filter maps directly to backend's 'status' param
    // Special values: 'contacted', 'not_contacted', 'existing_client' are handled by backend
    status: statusFilter !== 'all' ? statusFilter as LeadStatus : undefined,
    // Qualification status filter maps to backend's 'qualification_status' param
    qualification_status: qualificationFilter !== 'all' ? qualificationFilter as LeadFilters['qualification_status'] : undefined,
    bdm_email: salespersonFilter !== 'all' ? salespersonFilter : undefined,
    // Lead type filter for semantic types (newly_applied, newly_approved)
    lead_type: sourceFilter !== 'all' && LEAD_TYPE_VALUES.includes(sourceFilter) ? sourceFilter : undefined,
    // Source filter for specific data sources (fmcsa, manual, approved_30_days, etc.)
    source: sourceFilter !== 'all' && !LEAD_TYPE_VALUES.includes(sourceFilter) ? sourceFilter : undefined,
    // Date range filters
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    // Human review filter
    requires_review: requiresReviewFilter ? true : undefined,
    page,
    page_size: pageSize,
    sort_by: sortField,
    sort_order: sortOrder,
  }), [search, statusFilter, qualificationFilter, salespersonFilter, sourceFilter, dateFrom, dateTo, requiresReviewFilter, page, pageSize, sortField, sortOrder])

  // Fetch leads - optimized for performance
  // Increased stale time and refresh interval to reduce unnecessary re-renders
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['leads', filters],
    queryFn: () => leadsApi.getAll(filters).then(res => res.data),
    staleTime: 30000, // Consider data stale after 30 seconds (was 5s - too aggressive)
    refetchInterval: 60000, // Auto-refresh every 60 seconds (was 10s - caused UI stuttering)
    refetchIntervalInBackground: false, // Don't refresh when tab is in background
    placeholderData: (previousData) => previousData, // Keep showing previous data while loading new results
  })

  // Fetch salespersons for filter dropdown
  const { data: salespersonsData } = useQuery({
    queryKey: ['salespersons'],
    queryFn: () => leadsApi.getSalespersons().then(res => res.data),
    staleTime: 60000, // Cache for 1 minute
  })

  // Fetch users for reassignment dropdown
  const { data: usersData } = useQuery({
    queryKey: ['users', { is_active: true }],
    queryFn: () => usersApi.getAll({ is_active: true, page_size: 100 }).then(res => res.data),
    staleTime: 60000,
  })

  // Fetch review queue stats for badge count
  const { data: reviewStats } = useQuery({
    queryKey: ['leads-review-stats'],
    queryFn: () => leadsApi.getReviewStats().then(res => res.data),
    staleTime: 30000, // 30 second cache
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  })

  // Mutations with error handling
  const deleteMutation = useMutation({
    mutationFn: (id: string) => leadsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setShowDeleteDialog(false)
      setLeadToDelete(null)
      toast({ title: 'Lead deleted', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to delete lead', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  const bulkStatusMutation = useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: string }) =>
      leadsApi.bulkUpdateStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setSelectedIds(new Set())
      setShowBulkActionDialog(false)
      toast({ title: 'Status updated', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to update status', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => leadsApi.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setSelectedIds(new Set())
      setShowBulkActionDialog(false)
      toast({ title: 'Leads deleted', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to delete leads', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  const bulkPushMutation = useMutation({
    mutationFn: ({ ids, queue, options }: { ids: string[]; queue: string; options?: { lead_type?: string; channel?: string; bypass_cooldown?: boolean } }) =>
      leadsApi.bulkPush(ids, queue, options),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setSelectedIds(new Set())
      setShowBulkActionDialog(false)
      // Reset bulk push options
      setBulkPushLeadType('')
      setBulkPushChannel('')
      setBulkPushLanguage('')
      setBulkBypassCooldown(false)
      toast({ title: 'Pushed to queue', description: result.data.message, variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to push to queue', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  const bulkReassignMutation = useMutation({
    mutationFn: ({ ids, userId }: { ids: string[]; userId: string | null }) =>
      leadsApi.bulkReassign(ids, userId ? parseInt(userId, 10) : null),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['salespersons'] })
      setSelectedIds(new Set())
      setShowBulkActionDialog(false)
      setSelectedUserId(null)
      toast({ title: 'Leads reassigned', description: result.data.message, variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to reassign leads', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  const createLeadMutation = useMutation({
    mutationFn: (data: Partial<Lead>) => leadsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setShowAddDialog(false)
      resetAddLeadForm()
      toast({ title: 'Lead created', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to create lead', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  const singlePushMutation = useMutation({
    mutationFn: ({ id, queue, options }: { id: string; queue: string; options?: { lead_type?: string; channel?: string; bypass_cooldown?: boolean } }) =>
      leadsApi.pushToQueue(id, queue, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setShowPushDialog(false)
      setLeadToPush(null)
      toast({ title: 'Lead pushed to queue', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to push lead', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  const updateLeadMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Lead> }) =>
      leadsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setShowEditDialog(false)
      setLeadToEdit(null)
      toast({ title: 'Lead updated', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to update lead', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  // Approve lead from review queue
  const approveLeadMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      leadsApi.approve(id, { review_notes: notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['leads-review-stats'] })
      toast({ title: 'Lead approved', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to approve lead', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  // Reject lead from review queue
  const rejectLeadMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      leadsApi.reject(id, { review_notes: notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['leads-review-stats'] })
      toast({ title: 'Lead rejected', variant: 'success' })
    },
    onError: (error) => {
      toast({ title: 'Failed to reject lead', description: getErrorMessage(error), variant: 'destructive' })
    },
  })

  // Fetch activities for selected lead
  const { data: leadActivities, isLoading: activitiesLoading } = useQuery({
    queryKey: ['lead-activities', selectedLead?.id],
    queryFn: () => selectedLead ? activitiesApi.getByLead(selectedLead.id, { page_size: 20 }).then(res => res.data) : null,
    enabled: !!selectedLead && showDetailsDialog,
  })

  const resetAddLeadForm = () => {
    setAddLeadForm({
      company_name: '',
      contact_name: '',
      phone: '',
      email: '',
      mc_number: '',
      dot_number: '',
      state: '',
      city: '',
      zip_code: '',
      source: 'manual',
      notes: '',
      submit_to_qualifier: false,
    })
  }

  const handleCreateLead = () => {
    if (!addLeadForm.company_name) return

    const leadData: Partial<Lead> = {
      company_name: addLeadForm.company_name,
      contact_name: addLeadForm.contact_name || undefined,
      phone: addLeadForm.phone || undefined,
      email: addLeadForm.email || undefined,
      mc_number: addLeadForm.mc_number || undefined,
      dot_number: addLeadForm.dot_number || undefined,
      state: addLeadForm.state || undefined,
      city: addLeadForm.city || undefined,
      source: addLeadForm.source,
    }

    createLeadMutation.mutate(leadData)
  }

  const handlePushToQueue = (lead: Lead, queue?: string) => {
    if (queue === 'qualification') {
      // Direct push for qualification (no extra options)
      singlePushMutation.mutate({ id: lead.id, queue })
    } else {
      // Show dialog to choose queue and options (for engagement or generic push)
      setLeadToPush(lead)
      setSinglePushQueue(queue || 'engagement')
      setPushLeadType('')
      setPushChannel('')
      setPushLanguage('')
      setBypassCooldown(false)
      setShowPushDialog(true)
    }
  }

  const handleViewDetails = (lead: Lead) => {
    setSelectedLead(lead)
    setShowDetailsDialog(true)
  }

  const handleEditLead = (lead: Lead) => {
    setLeadToEdit(lead)
    setEditLeadForm({
      company_name: lead.company_name || '',
      contact_name: lead.contact_name || '',
      phone: lead.phone || '',
      email: lead.email || '',
      mc_number: lead.mc_number || '',
      dot_number: lead.dot_number || '',
      state: lead.state || '',
      city: lead.city || '',
      source: lead.source || '',
      notes: lead.notes || '',
      status: lead.status || 'new',
    })
    setShowEditDialog(true)
  }

  const handleUpdateLead = () => {
    if (!leadToEdit) return

    const updates: Partial<Lead> = {
      company_name: editLeadForm.company_name,
      contact_name: editLeadForm.contact_name || null,
      phone: editLeadForm.phone || null,
      email: editLeadForm.email || null,
      mc_number: editLeadForm.mc_number || null,
      dot_number: editLeadForm.dot_number || null,
      state: editLeadForm.state || null,
      city: editLeadForm.city || null,
      source: editLeadForm.source || null,
      notes: editLeadForm.notes || null,
      status: editLeadForm.status,
    }

    updateLeadMutation.mutate({ id: leadToEdit.id, data: updates })
  }

  const handleConfirmPush = () => {
    if (!leadToPush) return

    const options = singlePushQueue === 'engagement' ? {
      lead_type: pushLeadType || undefined,
      channel: pushChannel || undefined,
      preferred_language: pushLanguage || undefined,
      bypass_cooldown: bypassCooldown,
    } : undefined

    singlePushMutation.mutate({
      id: leadToPush.id,
      queue: singlePushQueue,
      options
    })
  }

  // Handlers
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('asc')
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked && data?.items) {
      setSelectedIds(new Set(data.items.map((lead: Lead) => lead.id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSet = new Set(selectedIds)
    if (checked) {
      newSet.add(id)
    } else {
      newSet.delete(id)
    }
    setSelectedIds(newSet)
  }

  const handleBulkAction = (action: 'status' | 'delete' | 'push' | 'reassign') => {
    setBulkAction(action)
    if (action === 'reassign') {
      setSelectedUserId(null)
    }
    setShowBulkActionDialog(true)
  }

  const executeBulkAction = () => {
    const ids = Array.from(selectedIds)
    if (bulkAction === 'status') {
      bulkStatusMutation.mutate({ ids, status: newStatus })
    } else if (bulkAction === 'delete') {
      bulkDeleteMutation.mutate(ids)
    } else if (bulkAction === 'push') {
      // Build options for engagement queue
      const options = pushQueue === 'engagement' ? {
        lead_type: bulkPushLeadType || undefined,
        channel: bulkPushChannel || undefined,
        preferred_language: bulkPushLanguage || undefined,
        bypass_cooldown: bulkBypassCooldown,
      } : undefined
      bulkPushMutation.mutate({ ids, queue: pushQueue, options })
    } else if (bulkAction === 'reassign') {
      bulkReassignMutation.mutate({ ids, userId: selectedUserId })
    }
  }

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronDown className="h-4 w-4 opacity-30" />
    return sortOrder === 'asc' ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    )
  }

  if (isLoading) {
    return <PageLoader />
  }

  const leads = data?.items || []
  const totalPages = data?.total_pages || 1
  const totalLeads = data?.total || 0
  const allSelected = leads.length > 0 && leads.every((lead: Lead) => selectedIds.has(lead.id))
  const someSelected = selectedIds.size > 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-500">Manage and track your sales leads</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowAddDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              {isFetching && searchInput ? (
                <Loader2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary animate-spin" />
              ) : (
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              )}
              <Input
                placeholder="Search by company, contact, phone, or email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value)
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <Filter className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={qualificationFilter}
                onValueChange={(value) => {
                  setQualificationFilter(value)
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Qualification" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Qualification</SelectItem>
                  {QUALIFICATION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={salespersonFilter}
                onValueChange={(value) => {
                  setSalespersonFilter(value)
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <User className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Salesperson" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Salespersons</SelectItem>
                  {salespersonsData?.salespersons?.map((sp) => (
                    <SelectItem key={sp.email} value={sp.email}>
                      {sp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={sourceFilter}
                onValueChange={(value) => {
                  setSourceFilter(value)
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-[160px]">
                  <Truck className="mr-2 h-4 w-4" />
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {SOURCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value)
                    setPage(1)
                  }}
                  className="w-[140px]"
                  placeholder="From"
                />
                <span className="text-gray-400">-</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value)
                    setPage(1)
                  }}
                  className="w-[140px]"
                  placeholder="To"
                />
                {(dateFrom || dateTo) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDateFrom('')
                      setDateTo('')
                      setPage(1)
                    }}
                    className="h-8 px-2"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <Button
                variant={requiresReviewFilter ? 'default' : 'outline'}
                size="sm"
                className={requiresReviewFilter ? 'bg-orange-600 hover:bg-orange-700' : ''}
                onClick={() => {
                  setRequiresReviewFilter(!requiresReviewFilter)
                  setPage(1)
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                Needs Review
                {reviewStats?.pending_review ? (
                  <Badge className="ml-2 bg-white text-orange-600 text-xs px-1.5 py-0">
                    {reviewStats.pending_review}
                  </Badge>
                ) : null}
              </Button>
              <Select
                value={pageSize.toString()}
                onValueChange={(value) => {
                  setPageSize(Number(value))
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size} rows
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bulk Actions */}
          {someSelected && (
            <div className="mt-4 flex flex-wrap items-center gap-2 sm:gap-4 rounded-lg bg-primary-50 p-3">
              <span className="text-sm font-medium text-primary-700 w-full sm:w-auto">
                {selectedIds.size} lead{selectedIds.size > 1 ? 's' : ''} selected
              </span>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction('status')}
                >
                  Change Status
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction('reassign')}
                >
                  <UserCog className="mr-2 h-4 w-4" />
                  Reassign
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleBulkAction('push')}
                >
                  Push to Queue
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleBulkAction('delete')}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelectedIds(new Set())}
              >
                Clear selection
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort('company_name')}
                >
                  <div className="flex items-center gap-1">
                    Company
                    <SortIcon field="company_name" />
                  </div>
                </TableHead>
                <TableHead>Contact</TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    <SortIcon field="status" />
                  </div>
                </TableHead>
                <TableHead>Location</TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort('quality_score')}
                >
                  <div className="flex items-center gap-1">
                    Score
                    <SortIcon field="quality_score" />
                  </div>
                </TableHead>
                <TableHead
                  className="cursor-pointer"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Created
                    <SortIcon field="created_at" />
                  </div>
                </TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center">
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <Building2 className="h-8 w-8 opacity-50" />
                      <p>No leads found</p>
                      <p className="text-sm">Try adjusting your filters</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow key={lead.id} className="group">
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(lead.id)}
                        onCheckedChange={(checked) =>
                          handleSelectOne(lead.id, checked as boolean)
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                          <Building2 className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {lead.company_name}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {lead.mc_number && (
                              <span>MC# {lead.mc_number}</span>
                            )}
                            {lead.dot_number && (
                              <span>DOT# {lead.dot_number}</span>
                            )}
                            {lead.truck_count && (
                              <span className="flex items-center gap-1">
                                <Truck className="h-3 w-3" />
                                {lead.truck_count}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {lead.contact_name && (
                          <div className="flex items-center gap-1 text-sm">
                            <User className="h-3 w-3 text-gray-400" />
                            {lead.contact_name}
                          </div>
                        )}
                        {lead.phone && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Phone className="h-3 w-3 text-gray-400" />
                            {lead.phone}
                          </div>
                        )}
                        {lead.email && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Mail className="h-3 w-3 text-gray-400" />
                            <span className="max-w-[180px] truncate">
                              {lead.email}
                            </span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={lead.status} variant="lead" />
                        {lead.requires_review && (
                          <Badge className="bg-orange-500 text-white text-xs">
                            <Eye className="h-3 w-3 mr-1" />
                            Review
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {(lead.city || lead.state) && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <MapPin className="h-3 w-3 text-gray-400" />
                          {[lead.city, lead.state].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {lead.quality_score !== null && (
                        <div
                          className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                            lead.quality_score >= 80
                              ? 'bg-green-100 text-green-700'
                              : lead.quality_score >= 50
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {lead.quality_score}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-500">
                        {formatRelativeTime(lead.created_at)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onSelect={() => handleViewDetails(lead)}>
                            <ExternalLink className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => handleEditLead(lead)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit Lead
                          </DropdownMenuItem>
                          {lead.requires_review && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-green-600"
                                onSelect={() => approveLeadMutation.mutate({ id: lead.id })}
                                disabled={approveLeadMutation.isPending}
                              >
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                Approve Lead
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onSelect={() => rejectLeadMutation.mutate({ id: lead.id })}
                                disabled={rejectLeadMutation.isPending}
                              >
                                <XCircle className="mr-2 h-4 w-4" />
                                Reject Lead
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => handlePushToQueue(lead, 'qualification')}>
                            <UserCheck className="mr-2 h-4 w-4 text-purple-600" />
                            Push to Qualification
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => handlePushToQueue(lead, 'engagement')}>
                            <MessageSquare className="mr-2 h-4 w-4 text-blue-600" />
                            Push to Engagement
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => handlePushToQueue(lead)}>
                            <Send className="mr-2 h-4 w-4" />
                            Push to Queue...
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onSelect={() => lead.phone && window.open(`tel:${lead.phone}`)}>
                            <Phone className="mr-2 h-4 w-4" />
                            Call Lead
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => lead.email && window.open(`mailto:${lead.email}`)}>
                            <Mail className="mr-2 h-4 w-4" />
                            Send Email
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onSelect={() => {
                              setLeadToDelete(lead)
                              setShowDeleteDialog(true)
                            }}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Showing {leads.length} of {totalLeads} leads
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Lead</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <strong>{leadToDelete?.company_name}</strong>? This action cannot
              be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => leadToDelete && deleteMutation.mutate(leadToDelete.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={showBulkActionDialog} onOpenChange={setShowBulkActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {bulkAction === 'status' && 'Change Status'}
              {bulkAction === 'delete' && 'Delete Leads'}
              {bulkAction === 'push' && 'Push to Queue'}
              {bulkAction === 'reassign' && 'Reassign Leads'}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === 'status' &&
                `Update the status of ${selectedIds.size} selected lead(s).`}
              {bulkAction === 'delete' &&
                `Are you sure you want to delete ${selectedIds.size} lead(s)? This action cannot be undone.`}
              {bulkAction === 'push' &&
                `Push ${selectedIds.size} lead(s) to an engagement queue.`}
              {bulkAction === 'reassign' &&
                `Reassign ${selectedIds.size} lead(s) to a different user.`}
            </DialogDescription>
          </DialogHeader>

          {bulkAction === 'status' && (
            <div className="py-4">
              <Select value={newStatus} onValueChange={(v) => setNewStatus(v as LeadStatus)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {bulkAction === 'push' && (
            <div className="space-y-4 py-4">
              <Select value={pushQueue} onValueChange={setPushQueue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select queue" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="qualification">Qualification Queue</SelectItem>
                  <SelectItem value="engagement">Engagement Queue (Calls)</SelectItem>
                </SelectContent>
              </Select>

              {/* Engagement Options - only shown for engagement queue */}
              {pushQueue === 'engagement' && (
                <div className="space-y-3">
                  {/* Lead Type Selection */}
                  <div className="p-3 bg-green-50 border border-green-200 rounded">
                    <Label className="text-sm font-medium text-green-800 mb-2 block">Lead Type (for script selection)</Label>
                    <Select value={bulkPushLeadType || 'default'} onValueChange={(v) => setBulkPushLeadType(v === 'default' ? '' : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Auto-detect from lead" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Auto-detect from lead</SelectItem>
                        <SelectItem value="newly_approved">Newly Approved MC</SelectItem>
                        <SelectItem value="newly_applied">Newly Applied MC</SelectItem>
                        <SelectItem value="approved_30_days">Approved 30 Days</SelectItem>
                        <SelectItem value="approved_60_days">Approved 60 Days</SelectItem>
                        <SelectItem value="approved_90_days">Approved 90 Days</SelectItem>
                        <SelectItem value="approved_180_days">Approved 180 Days</SelectItem>
                        <SelectItem value="existing_carrier">Existing Carrier</SelectItem>
                        <SelectItem value="staffing">Staffing Agency</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="construction">Construction</SelectItem>
                        <SelectItem value="wholesale_distribution">Wholesale Distribution</SelectItem>
                        <SelectItem value="oilfield_services">Oilfield Services</SelectItem>
                        <SelectItem value="government_contractor">Government Contractor</SelectItem>
                        <SelectItem value="general">General Lead</SelectItem>
                        <SelectItem value="other">Other Industry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Channel Selection */}
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                    <Label className="text-sm font-medium text-blue-800 mb-2 block">Contact Channel</Label>
                    <Select value={bulkPushChannel || 'auto'} onValueChange={(v) => setBulkPushChannel(v === 'auto' ? '' : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Auto (Phone preferred)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto (Phone preferred)</SelectItem>
                        <SelectItem value="phone">Phone Only</SelectItem>
                        <SelectItem value="sms">SMS Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Agent Language Selection */}
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                    <Label className="text-sm font-medium text-purple-800 mb-2 block">Agent Language</Label>
                    <Select value={bulkPushLanguage || 'default'} onValueChange={(v) => setBulkPushLanguage(v === 'default' ? '' : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Default (use lead's preference or English)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">Default (use lead's preference or English)</SelectItem>
                        <SelectItem value="en">English (Sarah Thompson)</SelectItem>
                        <SelectItem value="es">Spanish (Sofia Martinez)</SelectItem>
                        <SelectItem value="fr">French (Marie Dupont)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-purple-600 mt-1">Select language-specific agent for outbound calls</p>
                  </div>

                  {/* Bypass Cooldown */}
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="bulk_bypass_cooldown"
                        checked={bulkBypassCooldown}
                        onCheckedChange={(checked: boolean) => setBulkBypassCooldown(checked)}
                      />
                      <Label htmlFor="bulk_bypass_cooldown" className="text-sm text-yellow-800 cursor-pointer">
                        Bypass 24-hour cooldown (call even if recently contacted)
                      </Label>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {bulkAction === 'reassign' && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Assign to User</Label>
                <Select
                  value={selectedUserId || 'unassigned'}
                  onValueChange={(v) => setSelectedUserId(v === 'unassigned' ? null : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select user" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned (Remove assignment)</SelectItem>
                    {usersData?.items?.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name || user.email} {user.role === 'admin' && '(Admin)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Also show salespersons from leads for backward compatibility */}
              {salespersonsData?.salespersons && salespersonsData.salespersons.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-gray-500 mb-2">Or select from existing salespersons in leads:</p>
                  <div className="flex flex-wrap gap-1">
                    {salespersonsData.salespersons.slice(0, 10).map((sp) => (
                      <Button
                        key={sp.email}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => {
                          // Find matching user by email
                          const matchingUser = usersData?.items?.find(u => u.email.toLowerCase() === sp.email.toLowerCase())
                          if (matchingUser) {
                            setSelectedUserId(matchingUser.id)
                          }
                        }}
                      >
                        {sp.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkActionDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant={bulkAction === 'delete' ? 'destructive' : 'default'}
              onClick={executeBulkAction}
              disabled={
                bulkStatusMutation.isPending ||
                bulkDeleteMutation.isPending ||
                bulkPushMutation.isPending ||
                bulkReassignMutation.isPending
              }
            >
              {bulkAction === 'status' && 'Update Status'}
              {bulkAction === 'delete' && 'Delete Leads'}
              {bulkAction === 'push' && 'Push to Queue'}
              {bulkAction === 'reassign' && (bulkReassignMutation.isPending ? 'Reassigning...' : 'Reassign Leads')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open)
        if (!open) resetAddLeadForm()
      }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-green-600 text-white">
              <Plus className="h-6 w-6" />
              <DialogTitle className="text-white">Add New Lead</DialogTitle>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {/* Company Name (Required) */}
            <div className="col-span-2 md:col-span-3 space-y-1">
              <Label htmlFor="company_name">Company Name <span className="text-red-500">*</span></Label>
              <Input
                id="company_name"
                placeholder="Enter company name"
                value={addLeadForm.company_name}
                onChange={(e) => setAddLeadForm(prev => ({ ...prev, company_name: e.target.value }))}
              />
            </div>

            {/* Contact Name */}
            <div className="space-y-1">
              <Label htmlFor="contact_name">Contact Name</Label>
              <Input
                id="contact_name"
                placeholder="Primary contact"
                value={addLeadForm.contact_name}
                onChange={(e) => setAddLeadForm(prev => ({ ...prev, contact_name: e.target.value }))}
              />
            </div>

            {/* Phone */}
            <div className="space-y-1">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={addLeadForm.phone}
                onChange={(e) => setAddLeadForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>

            {/* Email */}
            <div className="space-y-1">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="contact@company.com"
                value={addLeadForm.email}
                onChange={(e) => setAddLeadForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            {/* MC Number */}
            <div className="space-y-1">
              <Label htmlFor="mc_number">MC Number</Label>
              <Input
                id="mc_number"
                placeholder="123456"
                value={addLeadForm.mc_number}
                onChange={(e) => setAddLeadForm(prev => ({ ...prev, mc_number: e.target.value }))}
              />
            </div>

            {/* DOT Number */}
            <div className="space-y-1">
              <Label htmlFor="dot_number">DOT Number</Label>
              <Input
                id="dot_number"
                placeholder="1234567"
                value={addLeadForm.dot_number}
                onChange={(e) => setAddLeadForm(prev => ({ ...prev, dot_number: e.target.value }))}
              />
            </div>

            {/* Source */}
            <div className="space-y-1">
              <Label htmlFor="source">Source</Label>
              <Select
                value={addLeadForm.source}
                onValueChange={(value) => setAddLeadForm(prev => ({ ...prev, source: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Entry</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="trade_show">Trade Show</SelectItem>
                  <SelectItem value="cold_call">Cold Call</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* City */}
            <div className="space-y-1">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                placeholder="Los Angeles"
                value={addLeadForm.city}
                onChange={(e) => setAddLeadForm(prev => ({ ...prev, city: e.target.value }))}
              />
            </div>

            {/* State */}
            <div className="space-y-1">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                placeholder="CA"
                maxLength={20}
                value={addLeadForm.state}
                onChange={(e) => setAddLeadForm(prev => ({ ...prev, state: e.target.value.toUpperCase() }))}
              />
            </div>

            {/* Zip Code */}
            <div className="space-y-1">
              <Label htmlFor="zip_code">Zip</Label>
              <Input
                id="zip_code"
                placeholder="90001"
                maxLength={10}
                value={addLeadForm.zip_code}
                onChange={(e) => setAddLeadForm(prev => ({ ...prev, zip_code: e.target.value }))}
              />
            </div>

            {/* Notes */}
            <div className="col-span-2 md:col-span-3 space-y-1">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                rows={2}
                placeholder="Additional notes..."
                value={addLeadForm.notes}
                onChange={(e) => setAddLeadForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            {/* Submit to Qualifier Checkbox */}
            <div className="col-span-2 md:col-span-3 flex items-center gap-2">
              <Checkbox
                id="submit_to_qualifier"
                checked={addLeadForm.submit_to_qualifier}
                onCheckedChange={(checked: boolean) => setAddLeadForm(prev => ({ ...prev, submit_to_qualifier: checked }))}
              />
              <Label htmlFor="submit_to_qualifier" className="text-sm cursor-pointer">
                Submit to qualification queue after creation
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateLead}
              disabled={!addLeadForm.company_name || createLeadMutation.isPending}
            >
              {createLeadMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Lead
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Push to Queue Dialog */}
      <Dialog open={showPushDialog} onOpenChange={setShowPushDialog}>
        <DialogContent className="max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Push Lead to Queue</DialogTitle>
            <DialogDescription>
              Push <strong>{leadToPush?.company_name}</strong> to agent queue
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1">
            <Select value={singlePushQueue} onValueChange={setSinglePushQueue}>
              <SelectTrigger>
                <SelectValue placeholder="Select queue" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="qualification">Qualification Queue</SelectItem>
                <SelectItem value="engagement">Engagement Queue</SelectItem>
                <SelectItem value="advisor">Advisor Queue</SelectItem>
              </SelectContent>
            </Select>

            {/* Engagement Options - only shown for engagement queue */}
            {singlePushQueue === 'engagement' && (
              <div className="space-y-3">
                {/* Lead Type Selection */}
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <Label className="text-sm font-medium text-green-800 mb-2 block">Lead Type (for script selection)</Label>
                  <Select value={pushLeadType || 'default'} onValueChange={(v) => setPushLeadType(v === 'default' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Use existing lead type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Use existing lead type</SelectItem>
                      <SelectItem value="newly_approved">Newly Approved MC</SelectItem>
                      <SelectItem value="newly_applied">Newly Applied MC</SelectItem>
                      <SelectItem value="approved_30_days">Approved 30 Days</SelectItem>
                      <SelectItem value="approved_60_days">Approved 60 Days</SelectItem>
                      <SelectItem value="approved_90_days">Approved 90 Days</SelectItem>
                      <SelectItem value="approved_180_days">Approved 180 Days</SelectItem>
                      <SelectItem value="existing_carrier">Existing Carrier</SelectItem>
                      <SelectItem value="staffing">Staffing Agency</SelectItem>
                      <SelectItem value="manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="construction">Construction</SelectItem>
                      <SelectItem value="wholesale_distribution">Wholesale Distribution</SelectItem>
                      <SelectItem value="oilfield_services">Oilfield Services</SelectItem>
                      <SelectItem value="government_contractor">Government Contractor</SelectItem>
                      <SelectItem value="general">General Lead</SelectItem>
                      <SelectItem value="other">Other Industry</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-green-600 mt-1">Override lead type for customized outbound script</p>
                </div>

                {/* Channel Selection */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                  <Label className="text-sm font-medium text-blue-800 mb-2 block">Contact Channel</Label>
                  <Select value={pushChannel || 'auto'} onValueChange={(v) => setPushChannel(v === 'auto' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Auto (system chooses)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (system chooses)</SelectItem>
                      <SelectItem value="phone">Phone Call</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-blue-600 mt-1">Choose how to contact this lead</p>
                </div>

                {/* Agent Language Selection */}
                <div className="p-3 bg-purple-50 border border-purple-200 rounded">
                  <Label className="text-sm font-medium text-purple-800 mb-2 block">Agent Language</Label>
                  <Select value={pushLanguage || 'default'} onValueChange={(v) => setPushLanguage(v === 'default' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Default (use lead's preference or English)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default (use lead's preference or English)</SelectItem>
                      <SelectItem value="en">English (Sarah Thompson)</SelectItem>
                      <SelectItem value="es">Spanish (Sofia Martinez)</SelectItem>
                      <SelectItem value="fr">French (Marie Dupont)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-purple-600 mt-1">Select language-specific agent for outbound calls</p>
                </div>

                {/* Bypass Cooldown Option */}
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="bypass_cooldown"
                      checked={bypassCooldown}
                      onCheckedChange={(checked: boolean) => setBypassCooldown(checked)}
                    />
                    <Label htmlFor="bypass_cooldown" className="text-sm text-yellow-800 cursor-pointer">
                      Bypass 24-hour cooldown (call even if recently contacted)
                    </Label>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPushDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPush}
              disabled={singlePushMutation.isPending}
            >
              {singlePushMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Pushing...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Push to Queue
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Lead Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-blue-600 text-white">
              <Building2 className="h-6 w-6" />
              <div className="flex-1">
                <DialogTitle className="text-white text-lg">{selectedLead?.company_name}</DialogTitle>
                <p className="text-blue-100 text-sm">{selectedLead?.contact_name || 'No contact name'}</p>
              </div>
              <StatusBadge status={selectedLead?.status || 'new'} variant="lead" />
            </div>
          </DialogHeader>

          {selectedLead && (
            <Tabs defaultValue="details" className="flex-1 overflow-hidden flex flex-col">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="details">Lead Details</TabsTrigger>
                <TabsTrigger value="activities">Activities ({leadActivities?.items?.length || 0})</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="flex-1 overflow-auto mt-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Contact Info */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <User className="h-4 w-4" /> Contact Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      {selectedLead.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-gray-400" />
                          <a href={`tel:${selectedLead.phone}`} className="text-blue-600 hover:underline">
                            {selectedLead.phone}
                          </a>
                        </div>
                      )}
                      {selectedLead.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-gray-400" />
                          <a href={`mailto:${selectedLead.email}`} className="text-blue-600 hover:underline">
                            {selectedLead.email}
                          </a>
                        </div>
                      )}
                      {(selectedLead.city || selectedLead.state) && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span>{[selectedLead.city, selectedLead.state].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Business Info */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <Hash className="h-4 w-4" /> Business Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      {selectedLead.mc_number && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">MC#:</span>
                          <span className="font-medium">{selectedLead.mc_number}</span>
                        </div>
                      )}
                      {selectedLead.dot_number && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">DOT#:</span>
                          <span className="font-medium">{selectedLead.dot_number}</span>
                        </div>
                      )}
                      {selectedLead.truck_count && (
                        <div className="flex items-center gap-2">
                          <Truck className="h-4 w-4 text-gray-400" />
                          <span>{selectedLead.truck_count} trucks</span>
                        </div>
                      )}
                      {selectedLead.quality_score !== null && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Quality Score:</span>
                          <Badge className={
                            selectedLead.quality_score >= 80 ? 'bg-green-500' :
                            selectedLead.quality_score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                          }>
                            {selectedLead.quality_score}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <Calendar className="h-4 w-4" /> Dates
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">Created:</span>
                        <span>{formatRelativeTime(selectedLead.created_at)}</span>
                      </div>
                      {selectedLead.last_contacted_at && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Last Contacted:</span>
                          <span>{formatRelativeTime(selectedLead.last_contacted_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Source & Notes */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900 flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Additional Info
                    </h4>
                    <div className="space-y-2 text-sm">
                      {selectedLead.source && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Source:</span>
                          <Badge variant="outline">{SOURCE_OPTIONS.find(s => s.value === selectedLead.source)?.label || selectedLead.source}</Badge>
                        </div>
                      )}
                      {selectedLead.bdm_email && (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">BDM:</span>
                          <span>{selectedLead.bdm_email}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes - full width */}
                  {selectedLead.notes && (
                    <div className="col-span-2 space-y-2">
                      <h4 className="font-medium text-gray-900">Notes</h4>
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{selectedLead.notes}</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="activities" className="flex-1 overflow-auto mt-4">
                {activitiesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : leadActivities?.items?.length ? (
                  <div className="h-[350px] overflow-auto">
                    <div className="space-y-3 pr-2">
                      {leadActivities.items.map((activity: ActivityType) => (
                        <div key={activity.id} className="flex items-start gap-3 p-3 border rounded-lg">
                          <div className={`mt-0.5 h-2 w-2 rounded-full ${
                            activity.activity_type?.toLowerCase().includes('call') ? 'bg-green-500' :
                            activity.activity_type?.toLowerCase().includes('email') ? 'bg-blue-500' :
                            'bg-gray-400'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">
                                {activity.activity_type}
                              </Badge>
                              {activity.outcome && (
                                <Badge variant="secondary" className="text-xs">
                                  {activity.outcome}
                                </Badge>
                              )}
                            </div>
                            {activity.subject && (
                              <p className="text-sm font-medium mt-1">{activity.subject}</p>
                            )}
                            {activity.notes && (
                              <p className="text-sm text-gray-600 mt-1">{activity.notes}</p>
                            )}
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {formatRelativeTime(activity.created_at)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                    <Activity className="h-8 w-8 opacity-50 mb-2" />
                    <p>No activities recorded</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowDetailsDialog(false)
              if (selectedLead) handleEditLead(selectedLead)
            }}>
              <Edit className="mr-2 h-4 w-4" />
              Edit Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => {
        setShowEditDialog(open)
        if (!open) setLeadToEdit(null)
      }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-center gap-3 p-4 -m-6 mb-4 rounded-t-lg bg-orange-600 text-white">
              <Edit className="h-6 w-6" />
              <DialogTitle className="text-white">Edit Lead</DialogTitle>
            </div>
          </DialogHeader>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {/* Company Name */}
            <div className="col-span-2 md:col-span-3 space-y-1">
              <Label htmlFor="edit_company_name">Company Name <span className="text-red-500">*</span></Label>
              <Input
                id="edit_company_name"
                value={editLeadForm.company_name}
                onChange={(e) => setEditLeadForm(prev => ({ ...prev, company_name: e.target.value }))}
              />
            </div>

            {/* Status */}
            <div className="space-y-1">
              <Label htmlFor="edit_status">Status</Label>
              <Select
                value={editLeadForm.status}
                onValueChange={(value) => setEditLeadForm(prev => ({ ...prev, status: value as LeadStatus }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Contact Name */}
            <div className="space-y-1">
              <Label htmlFor="edit_contact_name">Contact Name</Label>
              <Input
                id="edit_contact_name"
                value={editLeadForm.contact_name}
                onChange={(e) => setEditLeadForm(prev => ({ ...prev, contact_name: e.target.value }))}
              />
            </div>

            {/* Source */}
            <div className="space-y-1">
              <Label htmlFor="edit_source">Source</Label>
              <Select
                value={editLeadForm.source || 'none'}
                onValueChange={(value) => setEditLeadForm(prev => ({ ...prev, source: value === 'none' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No source</SelectItem>
                  {SOURCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Phone */}
            <div className="space-y-1">
              <Label htmlFor="edit_phone">Phone</Label>
              <Input
                id="edit_phone"
                type="tel"
                value={editLeadForm.phone}
                onChange={(e) => setEditLeadForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>

            {/* Email */}
            <div className="space-y-1">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={editLeadForm.email}
                onChange={(e) => setEditLeadForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>

            {/* MC Number */}
            <div className="space-y-1">
              <Label htmlFor="edit_mc_number">MC Number</Label>
              <Input
                id="edit_mc_number"
                value={editLeadForm.mc_number}
                onChange={(e) => setEditLeadForm(prev => ({ ...prev, mc_number: e.target.value }))}
              />
            </div>

            {/* DOT Number */}
            <div className="space-y-1">
              <Label htmlFor="edit_dot_number">DOT Number</Label>
              <Input
                id="edit_dot_number"
                value={editLeadForm.dot_number}
                onChange={(e) => setEditLeadForm(prev => ({ ...prev, dot_number: e.target.value }))}
              />
            </div>

            {/* City */}
            <div className="space-y-1">
              <Label htmlFor="edit_city">City</Label>
              <Input
                id="edit_city"
                value={editLeadForm.city}
                onChange={(e) => setEditLeadForm(prev => ({ ...prev, city: e.target.value }))}
              />
            </div>

            {/* State */}
            <div className="space-y-1">
              <Label htmlFor="edit_state">State</Label>
              <Input
                id="edit_state"
                maxLength={20}
                value={editLeadForm.state}
                onChange={(e) => setEditLeadForm(prev => ({ ...prev, state: e.target.value.toUpperCase() }))}
              />
            </div>

            {/* Notes */}
            <div className="col-span-2 md:col-span-3 space-y-1">
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                rows={3}
                value={editLeadForm.notes}
                onChange={(e) => setEditLeadForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateLead}
              disabled={!editLeadForm.company_name || updateLeadMutation.isPending}
            >
              {updateLeadMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Edit className="mr-2 h-4 w-4" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
